import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  clientsKeys,
  participantKeys,
  useClientByMemberId,
} from '@workspace/react-query'
import { Loader2 } from 'lucide-react'
import { authClient } from '@/lib/auth'
import { requireRoles } from '@/lib/middleware'
import { useTopNav } from '@/context/top-nav-provider'
import { Main } from '@/components/layout/main'
import { ClientOnboardingPreview } from '@/components/client-onboarding-form/onboarding-preview'

export const Route = createFileRoute(
  '/_authenticated/_organization/onboarding/preview'
)({
  beforeLoad: async () => requireRoles(['client', 'primary_contact']),
  component: OnboardingPreviewRoute,
})

function OnboardingPreviewRoute() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setLinks, setFixed } = useTopNav()
  const { data: activeMember } = authClient.useActiveMember()
  const { data: activeOrganization } = authClient.useActiveOrganization()
  const organizationId = activeOrganization?.id
  const memberId = activeMember?.id
  const { data: clientResponse, isLoading } = useClientByMemberId({
    organizationId: organizationId || null,
    memberId: memberId || null,
    enabled: !!organizationId && !!memberId,
  })

  const clientId = clientResponse?.data?.id

  React.useEffect(() => {
    setLinks([
      { title: 'Overview', href: '/' },
      { title: 'Onboarding Preview', href: '/onboarding/preview', isActive: true },
    ])
    setFixed(true)
    return () => {
      setLinks([])
    }
  }, [setLinks, setFixed])

  const handleSubmitSuccess = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: participantKeys.onboardingProgress(organizationId ?? null, null),
    })
    if (organizationId && clientId) {
      queryClient.invalidateQueries({
        queryKey: clientsKeys.detail(organizationId, clientId),
      })
      queryClient.invalidateQueries({
        queryKey: clientsKeys.lists(),
      })
    }
    navigate({ to: '/my-onboarding' })
  }, [organizationId, clientId, queryClient, navigate])

  if (isLoading) {
    return (
      <Main className='flex flex-1 items-center justify-center'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </Main>
    )
  }

  if (!organizationId || !clientId) {
    return (
      <Main className='flex flex-1 items-center justify-center'>
        <p className='text-muted-foreground'>Unable to load onboarding data</p>
      </Main>
    )
  }

  return (
    <Main className='flex flex-1 flex-col gap-6'>
      <ClientOnboardingPreview
        organizationId={organizationId}
        clientId={clientId}
        submittedByMemberId={memberId}
        onStartEditing={() => navigate({ to: '/onboarding/client' })}
        onSubmitSuccess={handleSubmitSuccess}
      />
    </Main>
  )
}
